import { withHydrate } from '@relay/react/hooks';
import { gtm } from '@helpers/dataLayer';
import { Device, from } from '@helpers/media';
import { withMotion } from '@hoc/withMotion';
import useProductSearch from '@hooks/useProductSearch';
import { useResize } from '@hooks/useResize';
import MenuBars from '@img/icons/header/menu-bars.svg';
import MenuX from '@img/icons/header/menu-x.svg';
import StoreLocator from '@img/icons/header/store-locator.svg';
import Logo from '@img/logos/brand/logo.svg';
import * as React from 'react';
import DesktopNavigation from './Components/DesktopNavigation/DesktopNavigation';
import MobileNavigation from './Components/MobileNavigation/MobileNavigation';
import SearchInput from './Components/SearchInput/SearchInput';
import SearchTypeahead from './Components/SearchTypeahead/SearchTypeahead';
import S from './Header.styles';
import headerVariants from './Header.variants';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const Takeover = /*#__PURE__*/React.lazy(() => import('@stories/Components/Algolia/Takeover/Takeover'));
const Header = props => {
  const [showDropdownOverlay, setShowDropdownOverlay] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchCategory, setSearchCategory] = React.useState(0);
  const [hasSearchTerm, setHasSearchTerm] = React.useState(false);
  const {
    doSearch,
    results
  } = useProductSearch(5, props.pageContext.documentCulture);
  const [device, setDevice] = React.useState('unknown');
  const abVariant = props.productsVariant ? props.productsVariant : 'B';
  const [dropdownOpenDesktop, setDropdownOpenDesktop] = React.useState(false);
  const [dropdownOpenMobile, setDropdownOpenMobile] = React.useState(false);
  const [algoliaTypeaheadResults, setAlgoliaTypeaheadResults] = React.useState([]);
  const storeLocatorLink = props.links.filter(link => link.type === 'store-locator')[0];
  const handleKeyPressCallback = React.useCallback(handleKeyPress, []);
  useResize({
    callback: updateDevice,
    debounce: 100,
    immediate: true,
    on: 'x'
  });
  React.useEffect(() => {
    // eslint-disable-next-line
    window.addEventListener('search', event => {
      setAlgoliaTypeaheadResults(event.detail);
    });
  }, []);

  // Add event listener when component mounts, to listen for key presses.
  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyPressCallback);
    return () => document.removeEventListener('keydown', handleKeyPressCallback);
  }, [handleKeyPressCallback]);
  React.useEffect(() => {
    if (!showDropdownOverlay) {
      return;
    }
    setIsSearchOpen(false);
  }, [showDropdownOverlay]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(S.HeaderGlobalStyles, {}), /*#__PURE__*/_jsxs(S.Wrapper, {
      "data-show-overlay": showDropdownOverlay || isSearchOpen && props.algoliaEnabled,
      "data-search-open": isSearchOpen,
      "data-dropdown-open": dropdownOpenDesktop || dropdownOpenMobile,
      "data-culture": props.pageContext.documentCulture,
      children: [/*#__PURE__*/_jsx(S.LinksContainer, {
        children: props.links && props.links.map((link, i) => /*#__PURE__*/_jsx(S.LinkItem, {
          children: /*#__PURE__*/_jsx(S.Link, {
            onClick: e => {
              if (link.type && link.type === 'store-locator') {
                e.preventDefault();
                gtm({
                  event: 'click_to_store_locator'
                });
                if (link.target === '_blank') {
                  window.open(link.url, '_blank');
                } else {
                  window.location.href = link.url;
                }
              }
            },
            href: link.url,
            target: link.target ?? '_self',
            children: link.text
          })
        }, `link-${i}: ${link.text}`))
      }), /*#__PURE__*/_jsxs(S.MainContainer, {
        children: [/*#__PURE__*/_jsx(S.Logo, {
          href: "/",
          "aria-label": "Milwaukee - Nothing but HEAVY DUTY.\u2122",
          children: /*#__PURE__*/_jsx(Logo, {})
        }), storeLocatorLink && /*#__PURE__*/_jsx(S.StoreLocatorLink, {
          "aria-label": "Store Locator",
          onClick: e => {
            e.preventDefault();
            gtm({
              event: 'click_to_store_locator'
            });
            if (storeLocatorLink.target === '_blank') {
              window.open(storeLocatorLink.url, '_blank');
            } else {
              window.location.href = storeLocatorLink.url;
            }
          },
          href: storeLocatorLink.url,
          target: storeLocatorLink.target ?? '_self',
          children: /*#__PURE__*/_jsx(StoreLocator, {})
        }), /*#__PURE__*/_jsxs(S.MenuButton, {
          "aria-current": mobileOpen,
          "aria-label": "Toggle Menu",
          onClick: () => {
            setIsSearchOpen(false);
            setMobileOpen(!mobileOpen);
          },
          children: [/*#__PURE__*/_jsx(MenuBars, {}), /*#__PURE__*/_jsx(MenuX, {})]
        }), /*#__PURE__*/_jsx(S.SiteNavigation, {
          children: /*#__PURE__*/_jsx(DesktopNavigation, {
            items: props.navigation,
            productsVariant: abVariant,
            translations: props.translations,
            setShowDropdownOverlay: setShowDropdownOverlay
          })
        }), renderSearch('desktop'), /*#__PURE__*/_jsxs(S.CloseSearchButton, {
          initial: "hidden",
          animate: isSearchOpen ? 'visible' : 'hidden',
          variants: headerVariants.opacityVariants,
          transition: headerVariants.globalTransition,
          onClick: resetSearch,
          children: [/*#__PURE__*/_jsx(MenuX, {}), props.translations['milwaukee.header.search.close']]
        }, "close")]
      }), renderSearch('mobile'), /*#__PURE__*/_jsx(MobileNavigation, {
        isOpen: mobileOpen,
        items: props.navigation,
        links: props.links,
        translations: props.translations
      }), !props.algoliaEnabled && /*#__PURE__*/_jsx(SearchTypeahead, {
        visible: isSearchOpen,
        hasSearchTerm: hasSearchTerm,
        results: results,
        cultureCode: props.pageContext.documentCulture,
        searchTerm: searchTerm,
        algoliaEnvironmentPrefix: props.algoliaEnvironmentPrefix
      })]
    }), isSearchOpen && props.algoliaEnabled ? /*#__PURE__*/_jsx(React.Suspense, {
      fallback: null,
      children: /*#__PURE__*/_jsx(Takeover, {
        translations: props.translations,
        searchTerm: searchTerm,
        searchCategory: props.searchCategories ? props.searchCategories[searchCategory] : undefined,
        setSearchTerm: setSearchTerm,
        helpfulLinks: props.helpfulLinks,
        popularProducts: props.popularProducts,
        popularCategories: props.popularCategories,
        popularSearchTerms: props.popularSearchTerms,
        featuredDocuments: props.featuredDocuments,
        searchTips: props.searchTips,
        mobileOpen: device === 'mobile',
        goToResults: goToResultsUrl,
        cultureCode: props.pageContext.documentCulture,
        algoliaEnvironmentPrefix: props.algoliaEnvironmentPrefix,
        searchDebounceMilliseconds: props.searchDebounceMilliseconds,
        articleNumberSearchDebounceMilliseconds: props.articleNumberSearchDebounceMilliseconds,
        minimumSearchCharacters: props.minimumSearchCharacters,
        algoliaDocumentsSearchEnabled: props.algoliaDocumentsSearchEnabled,
        algoliaQuerySuggestionsEnabled: props.algoliaQuerySuggestionsEnabled
      })
    }) : null]
  });

  /**
   * @description Renders the search component based on the device type.
   * @param deviceType The type of device ('desktop' or 'mobile') for which to render the search component.
   * @returns React.ReactNode The search component for the specified device type.
   */
  function renderSearch(deviceType) {
    const isMobileButton = deviceType === 'mobile' && props.algoliaEnabled;
    const isInlineSearch = deviceType === 'mobile' && !props.algoliaEnabled;
    const isHidden = isSearchOpen && props.algoliaEnabled && deviceType === 'mobile' || !props.algoliaEnabled && deviceType === 'desktop';
    return /*#__PURE__*/_jsx(S.Search, {
      initial: "initial",
      animate: isSearchOpen ? 'expanded' : 'initial',
      variants: headerVariants.searchVariants,
      transition: device === 'mobile' ? headerVariants.globalMobileTransition : headerVariants.globalTransition,
      "data-device": deviceType,
      "data-button": isMobileButton,
      "data-mobile-hidden": isHidden,
      onClick: () => {
        if (isMobileButton) {
          setMobileOpen(false);
          setIsSearchOpen(true);
        }
      },
      children: /*#__PURE__*/_jsx(SearchInput, {
        animatedPlaceholder: props.algoliaEnabled,
        placeholder: props.translations['milwaukee.header.search.placeholder'],
        listeningPlaceholder: props.translations['milwaukee.header.search.listening-placeholder'],
        ariaCurrent: isSearchOpen || deviceType === 'mobile',
        searchTerm: searchTerm,
        setSearchTerm: setSearchTerm,
        searchCategory: searchCategory,
        setSearchCategory: setSearchCategory,
        onFocus: () => {
          if (isInlineSearch) {
            return;
          }
          setIsSearchOpen(true);
        },
        searchCategories: props.searchCategories ?? [],
        dropdownOpen: deviceType === 'mobile' ? dropdownOpenMobile : dropdownOpenDesktop,
        setDropdownOpen: deviceType === 'mobile' ? setDropdownOpenMobile : setDropdownOpenDesktop,
        device: device,
        inputFocused: isSearchOpen && deviceType === 'desktop',
        onSearch: searchTerm => {
          if (props.algoliaEnabled) {
            return;
          }
          setHasSearchTerm(searchTerm.trim().length > 2);
          doSearch(searchTerm);
        },
        onSubmit: () => {
          if (isMobileButton) {
            return;
          }
          goToResultsUrl();
        }
      }, `search-input${deviceType === 'mobile' ? '-mobile' : ''}`)
    }, `search${deviceType === 'mobile' ? '-mobile' : ''}`);
  }

  /**
   * @description Handles the key press event and closes the search overlay if the 'esc' key is pressed.
   * @param event The key press event.
   */
  function handleKeyPress(event) {
    const focusedEl = document.activeElement;

    // Check if the pressed key is 'esc'
    if (event.key === 'Escape') {
      resetSearch();

      // Unfocus any element that is currently focused
      if (focusedEl) {
        focusedEl.blur();
      }
    }
  }

  /**
   * @description Redirects the user to the search results page.
   */
  function goToResultsUrl() {
    if (results.length === 1) {
      window.location.href = `${window.location.origin}${results[0].url}`;
      return;
    }

    // Check to see if any have a matching article number to search term
    if (algoliaTypeaheadResults.length > 0) {
      const product = algoliaTypeaheadResults[0];
      if (product.data.agilityIdMapping) {
        const agilityId = product.data.agilityIdMapping[searchTerm];
        if (agilityId && agilityId > 0) {
          window.location.href = `${product.url}?variant=${agilityId}`;
          return;
        }
      }
    }
    let url = `${window.location.origin}/sitefiles/handlers/searchRedirect.ashx/?searchTerm=${searchTerm}&culture=${props.pageContext.documentCulture}`;
    if (searchCategory > 0 && props.searchCategories && props.searchCategories[searchCategory].value.length > 0) {
      url += `&categories.mainCategories=${props.searchCategories[searchCategory].value}`;
    }
    window.location.href = url;
  }

  /**
   * @description Resets the search term and closes the search overlay.
   */
  function resetSearch() {
    setSearchTerm('');
    setIsSearchOpen(false);
    setHasSearchTerm(false);
  }

  /**
   * @description Updates the device state based on the current window size.
   */
  function updateDevice() {
    if (window.matchMedia(from(Device.DesktopSmall)).matches) {
      setDevice('desktop');
    } else {
      setDevice('mobile');
    }
  }
};
export const clientConfig = {
  hydrate: 'always'
};
export default withHydrate(withMotion(Header), 'Header', 'hybrid');
